import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BellRing,
  Building2,
  CheckCircle2,
  Clock,
  FileText,
  ShieldAlert,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

interface Kpis {
  range: { from: string; to: string };
  invoices: {
    issued: number;
    rejected: number;
    pending: number;
    draft: number;
    totalFaturado: number;
  };
  clients: { total: number };
  financial: { receitasPagas: number; aReceber: number; emAtraso: number };
  recentInvoices: Array<{
    id: string;
    numeroNfse: string | null;
    clientName: string | null;
    competencia: string;
    valor: number;
    status: 'DRAFT' | 'PENDING' | 'ISSUED' | 'REJECTED' | 'CANCELLED';
    createdAt: string;
  }>;
}

interface MonthlyPoint {
  month: string;
  faturado: number;
  emitidas: number;
}

interface Alerts {
  certificatesExpiringSoon: Array<{
    id: string;
    name: string;
    expiresAt: string;
    daysUntilExpiry: number;
  }>;
  invoicesRejectedLast24h: number;
  financialOverdue: number;
  certificatesExpired: number;
}

const STATUS_LABEL: Record<Kpis['recentInvoices'][number]['status'], string> = {
  DRAFT: 'Rascunho',
  PENDING: 'Em emissão',
  ISSUED: 'Emitida',
  REJECTED: 'Rejeitada',
  CANCELLED: 'Cancelada',
};
const STATUS_STYLE: Record<Kpis['recentInvoices'][number]['status'], string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  PENDING: 'bg-amber-500/10 text-amber-600',
  ISSUED: 'bg-emerald-500/10 text-emerald-600',
  REJECTED: 'bg-destructive/10 text-destructive',
  CANCELLED: 'bg-muted text-muted-foreground line-through',
};

function fmtMonth(s: string): string {
  // YYYY-MM → MMM/yy
  const [y, m] = s.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '') + `/${y!.slice(2)}`;
}

interface ReminderAlerta {
  id: string;
  descricao: string;
  fornecedor: string | null;
  diasRestantes: number;
  vencido: boolean;
}

export function DashboardPage() {
  const { user } = useAuth();

  const kpis = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: () => apiFetch<Kpis>('/dashboard/kpis'),
  });

  const monthly = useQuery({
    queryKey: ['dashboard', 'monthly'],
    queryFn: () => apiFetch<MonthlyPoint[]>('/dashboard/revenue-monthly?months=12'),
  });

  const alerts = useQuery({
    queryKey: ['dashboard', 'alerts'],
    queryFn: () => apiFetch<Alerts>('/dashboard/alerts'),
  });

  // Contas da casa que entraram na janela de aviso — VPS, domínio, licenças.
  const lembretes = useQuery({
    queryKey: ['reminders', 'alertas'],
    queryFn: () => apiFetch<ReminderAlerta[]>('/reminders/alertas'),
  });

  const chartData = (monthly.data ?? []).map((p) => ({ ...p, label: fmtMonth(p.month) }));
  const lembretesEmAlerta = lembretes.data ?? [];
  const hasAlerts =
    lembretesEmAlerta.length > 0 ||
    (!!alerts.data &&
      (alerts.data.certificatesExpiringSoon.length > 0 ||
        alerts.data.invoicesRejectedLast24h > 0 ||
        alerts.data.financialOverdue > 0 ||
        alerts.data.certificatesExpired > 0));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">
          Olá, {user?.fullName?.split(' ')[0] ?? 'usuário'}
        </h1>
        <p className="text-sm text-muted-foreground">
          Visão geral do mês atual. Faturado, emissões e pendências.
        </p>
      </header>

      {/* KPIs principais */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          icon={<CheckCircle2 className="h-4 w-4" />}
          label="NFSe emitidas (mês)"
          value={kpis.data?.invoices.issued.toString() ?? '—'}
          subtitle={kpis.data ? formatBRL(kpis.data.invoices.totalFaturado) : ''}
          tone="good"
        />
        <KpiCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Receitas pagas (mês)"
          value={kpis.data ? formatBRL(kpis.data.financial.receitasPagas) : '—'}
          tone="info"
        />
        <KpiCard
          icon={<Clock className="h-4 w-4" />}
          label="A receber (mês)"
          value={kpis.data ? formatBRL(kpis.data.financial.aReceber) : '—'}
          tone="warn"
        />
        <KpiCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Em atraso"
          value={kpis.data ? formatBRL(kpis.data.financial.emAtraso) : '—'}
          tone="bad"
        />
      </section>

      {/* Segunda linha de KPIs */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <SmallKpi
          icon={<Building2 className="h-4 w-4" />}
          label="Clientes"
          value={kpis.data?.clients.total ?? 0}
        />
        <SmallKpi
          icon={<FileText className="h-4 w-4" />}
          label="Rascunhos"
          value={kpis.data?.invoices.draft ?? 0}
        />
        <SmallKpi
          icon={<Clock className="h-4 w-4" />}
          label="Em emissão"
          value={kpis.data?.invoices.pending ?? 0}
        />
        <SmallKpi
          icon={<XCircle className="h-4 w-4" />}
          label="Rejeitadas (mês)"
          value={kpis.data?.invoices.rejected ?? 0}
        />
      </section>

      {/* Alertas */}
      {hasAlerts && (
        <section className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-700">
            <AlertTriangle className="h-4 w-4" /> Alertas
          </div>
          <ul className="space-y-1 text-sm">
            {lembretesEmAlerta.map((r) => (
              <li key={r.id} className="flex items-center gap-2">
                <BellRing
                  className={`h-3 w-3 ${r.vencido ? 'text-destructive' : 'text-amber-600'}`}
                />
                <strong>{r.descricao}</strong>
                {r.fornecedor ? ` (${r.fornecedor})` : ''}{' '}
                {r.vencido
                  ? `está ${Math.abs(r.diasRestantes)} dia(s) atrasado.`
                  : r.diasRestantes === 0
                    ? 'vence hoje.'
                    : `vence em ${r.diasRestantes} dia(s).`}{' '}
                <Link to="/reminders" className="text-primary hover:underline">
                  Ver
                </Link>
              </li>
            ))}
            {alerts.data && alerts.data.certificatesExpired > 0 && (
              <li className="flex items-center gap-2">
                <ShieldAlert className="h-3 w-3 text-destructive" />
                <strong>{alerts.data.certificatesExpired}</strong> certificado(s) vencido(s).{' '}
                <Link to="/certificates" className="text-primary hover:underline">
                  Ver
                </Link>
              </li>
            )}
            {(alerts.data?.certificatesExpiringSoon ?? []).map((c) => (
              <li key={c.id} className="flex items-center gap-2">
                <ShieldAlert className="h-3 w-3 text-amber-600" />
                Certificado <strong>{c.name}</strong> expira em <strong>{c.daysUntilExpiry}</strong>{' '}
                dia(s).{' '}
                <Link to="/certificates" className="text-primary hover:underline">
                  Ver
                </Link>
              </li>
            ))}
            {alerts.data && alerts.data.invoicesRejectedLast24h > 0 && (
              <li className="flex items-center gap-2">
                <XCircle className="h-3 w-3 text-destructive" />
                <strong>{alerts.data.invoicesRejectedLast24h}</strong> nota(s) rejeitada(s) nas
                últimas 24h.{' '}
                <Link to="/invoices" className="text-primary hover:underline">
                  Ver
                </Link>
              </li>
            )}
            {alerts.data && alerts.data.financialOverdue > 0 && (
              <li className="flex items-center gap-2">
                <AlertTriangle className="h-3 w-3 text-destructive" />
                <strong>{alerts.data.financialOverdue}</strong> lançamento(s) em atraso.{' '}
                <Link to="/financial" className="text-primary hover:underline">
                  Ver
                </Link>
              </li>
            )}
          </ul>
        </section>
      )}

      {/* Gráfico */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-medium mb-3">Faturamento — últimos 12 meses</h2>
        {monthly.isLoading && (
          <p className="text-sm text-muted-foreground">Carregando gráfico…</p>
        )}
        {chartData.length > 0 && (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(v: number) =>
                    new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)
                  }
                />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 6,
                  }}
                  formatter={(v: number) => formatBRL(v)}
                />
                <Bar dataKey="faturado" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Últimas notas */}
      <section className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-4 py-2 flex items-center justify-between">
          <h2 className="text-sm font-medium">Últimas notas</h2>
          <Link to="/invoices" className="text-xs text-primary hover:underline">
            Ver todas
          </Link>
        </div>
        {kpis.data && kpis.data.recentInvoices.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">Sem notas ainda.</p>
        )}
        {kpis.data && kpis.data.recentInvoices.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Número</th>
                <th className="px-4 py-2 font-medium">Cliente</th>
                <th className="px-4 py-2 font-medium">Competência</th>
                <th className="px-4 py-2 font-medium">Valor</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {kpis.data.recentInvoices.map((inv) => (
                <tr key={inv.id} className="border-t border-border">
                  <td className="px-4 py-2 font-mono text-xs">{inv.numeroNfse ?? '—'}</td>
                  <td className="px-4 py-2">{inv.clientName ?? '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs">{inv.competencia.slice(0, 7)}</td>
                  <td className="px-4 py-2">{formatBRL(inv.valor)}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[inv.status]}`}
                    >
                      {STATUS_LABEL[inv.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  subtitle,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  tone: 'good' | 'warn' | 'bad' | 'info';
}) {
  const tones: Record<typeof tone, string> = {
    good: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700',
    warn: 'border-amber-500/30 bg-amber-500/5 text-amber-700',
    bad: 'border-destructive/30 bg-destructive/5 text-destructive',
    info: 'border-border bg-card text-foreground',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-xl font-semibold">{value}</div>
      {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
    </div>
  );
}

function SmallKpi({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
